import { createSelector } from 'reselect';

import {
  messages,
  PAGE_PATHNAMES,
  PageType,
  PrivacyCenter,
} from '@main/pc-types';
import { createDhEncryptionKeys } from '@main/sm-browser';
import { switchCase } from '@main/utils';

import { PrivacyCenterState } from './slice';

/**
 * The selector for the `privacy-center` state store slice
 */
export const getPrivacyCenterSlice = (state: any): PrivacyCenterState =>
  state['privacy-center'];

/**
 * A memoized selector for the user's auth code
 */
export const selectAuthSecret = createSelector(
  getPrivacyCenterSlice,
  ({ authSecret }) => authSecret,
);

/**
 * The type of authentication that the auth secret holds
 */
export const selectAuthMethod = createSelector(
  getPrivacyCenterSlice,
  ({ authMethod }) => authMethod,
);

/**
 * The cached public key for sombra
 */
export const selectSombraPublicKey = createSelector(
  getPrivacyCenterSlice,
  ({ sombraPublicKey }) => sombraPublicKey,
);

/**
 * A memoized selector for the profile of the logged in user on the privacy center
 */
export const selectSessionProfile = createSelector(
  getPrivacyCenterSlice,
  ({ sessionProfile }) => sessionProfile,
);

/**
 * A memoized selector for whether someone is actively logging in
 */
export const selectIsLoggingIn = createSelector(
  getPrivacyCenterSlice,
  ({ isLoggingIn }) => isLoggingIn,
);

/**
 * A memoized selector for the profile of the logged in user on then privacy center
 */
export const selectCoreIdentifier = createSelector(
  getPrivacyCenterSlice,
  ({ coreIdentifier }) => coreIdentifier,
);

/**
 * The privacyCenter configuration
 */
export const selectPrivacyCenter = createSelector(
  getPrivacyCenterSlice,
  ({ privacyCenter }) => privacyCenter,
);

/**
 * The privacyCenter white label configuration
 */
export const selectWhiteLabelPrivacyCenter = createSelector(
  getPrivacyCenterSlice,
  ({ whiteLabelPrivacyCenter }) => whiteLabelPrivacyCenter,
);

/**
 * The privacyCenter disabling of about transcend page
 */
export const selectDisabledPrivacyCenterAboutTranscend = createSelector(
  getPrivacyCenterSlice,
  ({ whiteLabelPrivacyCenter, disabledPrivacyCenterAboutTranscend }) =>
    whiteLabelPrivacyCenter || disabledPrivacyCenterAboutTranscend,
);

/**
 * Select the current consent partition value
 */
export const selectCurrentPartition = createSelector(
  getPrivacyCenterSlice,
  ({ partition, privacyCenter }) =>
    partition || privacyCenter?.consentManagers?.[0]?.id,
);

/**
 * The privacyCenter configuration, throwing an error if it does not exist
 */
export const selectRequiredPrivacyCenter = createSelector(
  selectPrivacyCenter,
  (privacyCenter) => {
    if (!privacyCenter) {
      throw new Error('Expected to find privacyCenter in the store');
    }
    return privacyCenter;
  },
);

/**
 * The pages to render on the privacy center
 */
export const selectPrivacyCenterPages = createSelector(
  selectRequiredPrivacyCenter,
  (privacyCenter: PrivacyCenter) =>
    Object.values(privacyCenter.pages)
      .filter(({ type }) =>
        switchCase<boolean, [], PageType>({
          [PageType.ManageYourPrivacy]: privacyCenter.showManageYourPrivacy,
          [PageType.SaleOfInfo]: privacyCenter.showSaleOfInfo,
          [PageType.HomePage]: false,
          [PageType.Policies]: privacyCenter.showPolicies,
          [PageType.TrackingTechnologies]:
            privacyCenter.showTrackingTechnologies,
          [PageType.RequestsProcessedReporting]:
            privacyCenter.showRequestsProcessedStats,
          [PageType.CommunicationsPreferences]:
            privacyCenter.showMarketingPreferences,
        })(true)(type),
      )
      .map(({ type }) => ({
        href: PAGE_PATHNAMES[type],
        message: messages[type],
      }))
      .filter(({ message, href }) => !!message && !!href),
);

/**
 * Indicator as to whether there is someone actively logged into the privacy center
 */
export const selectIsLoggedIn = createSelector(
  selectSessionProfile,
  (sessionProfile) => !!sessionProfile,
);

/**
 * Select the diffie hellman encryption keys
 */
export const selectDhEncryptionKeys = createSelector(
  selectSombraPublicKey,
  (sombraPublicKey) =>
    sombraPublicKey ? createDhEncryptionKeys(sombraPublicKey) : undefined,
);

/**
 * A memoized selector for the child org profiles of the logged in user on the privacy center
 */
export const selectChildSessionProfiles = createSelector(
  getPrivacyCenterSlice,
  ({ childSessionProfiles }) => childSessionProfiles,
);

export const selectWorkflowsV2Enabled = createSelector(
  getPrivacyCenterSlice,
  ({ workflowsV2Enabled }) => workflowsV2Enabled,
);
